/**
 * Filter der Spalte „Ware eingebucht" (Asana „Ware eingebucht Knopf - Sehr wichtig fuer
 * Steuerberater", Auftrag 2026-10-20) - NEUBAU ohne Bestandsvorbild. Zwei Haelften in einem Menue:
 *
 * 1. Datumsbereich Von/Bis auf den Tag der LETZTEN Einbuchung (`DatumsBereichFilter`, ohne „Nur ohne
 *    Wert": „nicht eingebucht" in der Zustandsauswahl deckt das ab, und eine Zeile ohne Datum faellt
 *    bei gesetztem Bereich ohnehin heraus, siehe `lib/orderList.ts#orderListZeilePasstFilter`),
 * 2. Mehrfachauswahl der Zustaende vollstaendig / teilweise / nicht eingebucht.
 *
 * Ist KEIN Zustand eingeschraenkt (`wirksameZustaende` -> `null`), zeigt das Widget alle drei
 * angehakt - "alle an" ist der Vorgabezustand und schraenkt nichts ein. Die letzte noch angehakte
 * Option laesst sich nicht abwaehlen: eine leere Auswahl wuerde dasselbe bedeuten wie "alle", sah
 * aber nach "nichts" aus. Wer eine Option loswerden will, hakt erst eine andere an.
 *
 * Der Filterwert bleibt ein `EingebuchtFilter`; ob er im Board-Zustand verbleibt, entscheidet
 * `BoardSeite` ueber `orderListSpaltenfilterAktiv` (alle drei Zustaende und kein Datum -> Eintrag
 * entfaellt).
 */
import { DatumsBereichFilter } from "./DatumsBereichFilter.tsx";
import { EINGEBUCHT_ZUSTAENDE, wirksameZustaende } from "../../../lib/orderList.ts";
import type { EingebuchtFilter as EingebuchtFilterWert, EingebuchtZustand } from "../../../lib/orderList.ts";

/** Anzeigetexte der drei Zustaende, in der Reihenfolge von `EINGEBUCHT_ZUSTAENDE`. */
const BESCHRIFTUNG: Readonly<Record<EingebuchtZustand, string>> = {
  vollstaendig: "vollständig",
  teilweise: "teilweise",
  nichts: "nicht eingebucht",
};

export interface EingebuchtFilterProps {
  /** Spaltenname fuer `aria-label`/Gruppenbeschriftung. */
  label: string;
  filter: EingebuchtFilterWert;
  onAendern: (filter: EingebuchtFilterWert) => void;
}

export function EingebuchtFilter({ label, filter, onAendern }: EingebuchtFilterProps) {
  const eingeschraenkt = wirksameZustaende(filter);
  const angehakt: readonly EingebuchtZustand[] = eingeschraenkt ?? EINGEBUCHT_ZUSTAENDE;

  const toggeln = (zustand: EingebuchtZustand): void => {
    const neu = angehakt.includes(zustand)
      ? angehakt.filter((z) => z !== zustand)
      : EINGEBUCHT_ZUSTAENDE.filter((z) => angehakt.includes(z) || z === zustand);
    if (neu.length === 0) return;
    onAendern({ ...filter, zustaende: neu });
  };

  return (
    <div className="spaltenfilter-eingebucht">
      <DatumsBereichFilter
        label={label}
        bereich={{ von: filter.von, bis: filter.bis }}
        onAendern={(bereich) => onAendern({ ...filter, von: bereich.von, bis: bereich.bis })}
        ohneWertVerstecken
      />
      <div className="spaltenfilter-optionen" role="group" aria-label={`${label}-Zustände`}>
        {EINGEBUCHT_ZUSTAENDE.map((zustand) => {
          const gesperrt = eingeschraenkt !== null && eingeschraenkt.length === 1 && angehakt.includes(zustand);
          return (
            <label key={zustand} className="spaltenfilter-option">
              <input
                type="checkbox"
                aria-label={`${label}: ${BESCHRIFTUNG[zustand]}`}
                checked={angehakt.includes(zustand)}
                disabled={gesperrt}
                title={gesperrt ? "Mindestens ein Zustand muss angehakt bleiben" : undefined}
                onChange={() => toggeln(zustand)}
              />
              <span>{BESCHRIFTUNG[zustand]}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
